import React, { useEffect, useState } from "react";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../../../components/ui/toggle-group";
import { Label } from "../../../components/ui/label";
import { Slider } from "../../../components/ui/slider";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../components/ui/radio-group";
import { useQuery } from "react-query";
import { wooProduct } from "../../products/productInterface";
import { wooCategory } from "../../../../src/lib/interfaces";
import { decodeHTML } from "../../../../src/lib/utils";
import { WooCommerce } from "../../../../src/lib/api";
import Products from "../../products/Products";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../src/components/ui/accordion";
import { Badge } from "../../../../src/components/ui/badge";
import { X } from "lucide-react";
import { Input } from "../../../../src/components/ui/input";
import "./sidebar.css";
import { useNavigate } from "react-router-dom";
import { ShopHeader } from "./ShopHeader";
import { Button } from "../../../components/ui/button";

interface ShopSidebarProps {
  allProducts: wooProduct[];
  categoryId: string;
  categoryName: string;
  searchQuery: string;
}

export default function ShopSidebar({
  allProducts,
  categoryId,
  categoryName,
  searchQuery,
}: ShopSidebarProps) {
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(100);
  const [sliderMin, setSliderMin] = useState(0);
  const [sliderMax, setSliderMax] = useState(100);
  const [selectedCategories, setSelectedCategories] =
    useState<string[]>(categoryId ? [categoryId] : []);
  const [sortOption, setSortOption] = useState("abc");
  const nav = useNavigate();
  const { data: categories } = useQuery(
    ["wooCategories"],
    async () => {
      try {
        const response = await WooCommerce.get(
          "products/categories/?per_page=100"
        );
        return response.data;
      } catch (error: any) {
        console.error(
          "Error fetching categories:",
          error?.response?.data || error
        );
        return [];
      }
    }
  );
  const processProducts = (options: {
    sortOption?: string;
    sliderMin?: number;
    sliderMax?: number;
    categories?: string[];
  }): wooProduct[] => {
    let result = [...allProducts];

    // Filter by price
    if (
      options.sliderMin !== undefined &&
      options.sliderMax !== undefined
    ) {
      result = result?.filter((product) => {
        const price = parseFloat(product.price);
        return (
          !isNaN(price) &&
          price >= options.sliderMin! &&
          price <= options.sliderMax!
        );
      });
    }

    const brandCategoryIds = categories
      ?.filter((cat: wooCategory) => cat.parent === 486)
      ?.map((cat: wooCategory) => String(cat.id));
    if (
      options.categories &&
      options.categories.length > 0
    ) {
      const selectedCategoryIds =
        options.categories?.map(String); // normalize to strings

      const brandCategoryIdsStr =
        brandCategoryIds?.map(String);
      const selectedBrands = selectedCategoryIds.filter(
        (id) => brandCategoryIdsStr?.includes(id)
      );

      const selectedRegular = selectedCategoryIds.filter(
        (id) => !brandCategoryIds?.includes(id)
      );

      result = result.filter((product) => {
        const productCatIds = product.categories?.map(
          (cat) => String(cat.id)
        );

        if (selectedBrands.length > 0) {
          const hasAllBrands = selectedBrands.some(
            (id: string) => productCatIds?.includes(id)
          );
          const hasAnyRegular =
            selectedRegular.length === 0 ||
            selectedRegular?.some((id) =>
              productCatIds?.includes(id)
            );

          return hasAllBrands && hasAnyRegular;
        }

        // No brands selected: regular OR logic
        return productCatIds.some((id) =>
          selectedCategoryIds?.includes(id)
        );
      });
    }

    // Sorting
    switch (options.sortOption) {
      case "asc":
        result.sort(
          (a, b) =>
            parseFloat(a.price) - parseFloat(b.price)
        );
        break;
      case "desc":
        result.sort(
          (a, b) =>
            parseFloat(b.price) - parseFloat(a.price)
        );
        break;
      case "abc":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "cba":
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      // default: do nothing
    }

    return result;
  };
  const handleCheckedCategoriesChange = (
    checked: boolean,
    categoryId: string
  ) => {
    setSelectedCategories((prev) =>
      checked
        ? [...prev, categoryId]
        : prev.filter((id) => id !== categoryId)
    );
  };

  const setSliderPrices = React.useCallback(
    (products: wooProduct[]) => {
      const prices = products
        ?.map((p) => parseFloat(p.price))
        .filter((p) => !isNaN(p));
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      setPriceMin(min);
      setPriceMax(max);
      if (sliderMin < min) setSliderMin(min);
      if (sliderMax > max) setSliderMax(max);
    },
    [sliderMax, sliderMin]
  );

  useEffect(() => {
    if (!allProducts) return;
    if (allProducts.length === 0) {
      return;
    }
    setSliderPrices(allProducts);
  }, [allProducts]);

  const handleSliderChange = ([min, max]: number[]) => {
    setSliderMin(min);
    setSliderMax(max);
  };

  const handleCategoriesChange = (value: string[]) => {
    setSelectedCategories(value);
  };

  const handleSortOptionChange = (value: string) => {
    setSortOption(value);
  };

  return (
    <div className="hidden lg:flex">
      <aside className="w-[450px] space-y-8 p-6 bg-card rounded-none border-r sticky top-72 left-0 overflow-scroll no-scrollbar max-h-[700px]">
        {/* Sort Options */}
        <ShopHeader
          categoryName={categoryName}
          categoryId={categoryId}
        />

        <div className="space-y-4">
          <h3 className="font-semibold">Sort By</h3>
          <RadioGroup
            value={sortOption}
            onValueChange={handleSortOptionChange}
            className="space-y-2"
          >
            {[
              { value: "abc", label: "Name: A to Z" },
              { value: "cba", label: "Name: Z to A" },
              { value: "asc", label: "Price: Low to High" },
              {
                value: "desc",
                label: "Price: High to Low",
              },
            ].map(({ value, label }) => (
              <div
                key={value}
                className="flex items-center space-x-2"
              >
                <RadioGroupItem value={value} id={value} />
                <Label
                  htmlFor={value}
                  className={`${
                    value === sortOption ? "font-bold" : ""
                  }`}
                >
                  {label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Price Range */}
        <div className="space-y-4">
          <h3 className="font-semibold">Price Range</h3>
          <Slider
            value={[sliderMin, sliderMax]}
            onValueChange={handleSliderChange}
            min={priceMin}
            max={priceMax}
            step={1}
            className="w-full rounded-none"
          />
          <div className="flex justify-between text-sm">
            <span>${sliderMin}</span>
            <span>${sliderMax}</span>
          </div>
        </div>

        <Accordion type="single" collapsible>
          <AccordionItem value="brands">
            <AccordionTrigger>Brands</AccordionTrigger>
            <AccordionContent>
              <ToggleGroup
                type="multiple"
                value={selectedCategories}
                onValueChange={handleCategoriesChange}
                className="flex flex-col gap-5 rounded-none"
              >
                {categories
                  ?.filter(
                    (cat: wooCategory) =>
                      cat.id === 502 || cat.id === 368
                  )
                  .map((cat: wooCategory) => (
                    <ToggleGroupItem
                      key={cat.id}
                      value={String(cat.id)}
                      variant="outline"
                      className="w-full justify-start rounded-none data-[state=on]:bg-neutral-100"
                    >
                      {decodeHTML(cat.name)}
                    </ToggleGroupItem>
                  ))}
              </ToggleGroup>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Categories */}
        {[
          { id: 487, title: "Hair Care" },
          { id: 489, title: "Hair Concern" },
        ].map(({ id, title }) => (
          <Accordion type="single" collapsible key={id}>
            <AccordionItem value={title}>
              <AccordionTrigger>{title}</AccordionTrigger>
              <AccordionContent className="flex gap-5 flex-col">
                {categories
                  ?.filter(
                    (cat: wooCategory) => cat.parent === id
                  )
                  ?.map((cat: wooCategory) => (
                    <div
                      key={cat.id}
                      className="flex items-center space-x-2 "
                    >
                      <Checkbox
                        id={`category-${cat.id}`}
                        checked={selectedCategories?.includes(
                          String(cat.id)
                        )}
                        onCheckedChange={(checked) =>
                          handleCheckedCategoriesChange(
                            Boolean(checked),
                            String(cat.id)
                          )
                        }
                        className="rounded-none"
                      />
                      <Label htmlFor={`category-${cat.id}`}>
                        {decodeHTML(cat.name)}
                      </Label>
                    </div>
                  ))}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ))}
        <Accordion type="single" collapsible>
          <AccordionItem value="accessories">
            <AccordionTrigger>Accessories</AccordionTrigger>
            <AccordionContent className="flex flex-col gap-5">
              {categories
                ?.filter(
                  (cat: wooCategory) => cat.parent === 505
                )
                .map((category: wooCategory) => (
                  <div
                    key={category.id}
                    className="flex items-center space-x-2 "
                  >
                    <Checkbox
                      id={`category-${category.id}`}
                      checked={selectedCategories?.includes(
                        String(category.id)
                      )}
                      onCheckedChange={(checked) =>
                        handleCheckedCategoriesChange(
                          Boolean(checked),
                          String(category.id)
                        )
                      }
                      className="rounded-none"
                    />
                    <Label
                      htmlFor={`category-${category.id}`}
                    >
                      {decodeHTML(category.name)}
                    </Label>
                  </div>
                ))}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </aside>

      {processProducts({
        sortOption,
        sliderMin,
        sliderMax,
        categories: selectedCategories,
      }).length > 0 ? (
        <div className="flex flex-col w-full justify-start items-center mt-16">
          {selectedCategories.length > 0 && (
            <div className="flex gap-5 flex-wrap w-full pl-5 pr-5">
              {categories
                ?.filter((cat: wooCategory) =>
                  selectedCategories?.includes(
                    String(cat.id)
                  )
                )
                ?.map((cat: wooCategory) => (
                  <Badge
                    key={cat.id}
                    className="text-sm flex gap-2 rounded-none"
                    variant={"outline"}
                  >
                    <span>{decodeHTML(cat.name)}</span>
                    <button>
                      <X
                        className="size-3 hover:text-neutral-400"
                        onClick={() => {
                          setSelectedCategories(
                            selectedCategories.filter(
                              (id) => id !== String(cat.id)
                            )
                          );
                          if (
                            categoryId === String(cat.id)
                          ) {
                            nav(`/shop`);
                          }
                        }}
                      />
                    </button>
                  </Badge>
                ))}
            </div>
          )}
          {searchQuery !== "" && (
            <div className="flex gap-2 justify-center items-center">
              <span>Search Results for</span>
              <div className="flex gap-2 justify-center items-center border p-1">
                <span className="font-bold">
                  {searchQuery}
                </span>
                <X
                  className="size-3 cursor-pointer"
                  onClick={() => nav("/shop")}
                />
              </div>
            </div>
          )}
          <Products
            search={searchQuery || ""}
            products={processProducts({
              sortOption,
              sliderMin,
              sliderMax,
              categories: selectedCategories,
            })}
          />
        </div>
      ) : (
        <div className="flex flex-col justify-start gap-10 items-center w-screen h-100 mt-16">
          <div className="flex gap-5 flex-wrap w-full pl-5 pr-5 mb-5">
            {categories
              ?.filter((cat: wooCategory) =>
                selectedCategories?.includes(String(cat.id))
              )
              ?.map((cat: wooCategory) => (
                <Badge
                  key={cat.id}
                  className="text-sm flex gap-2 rounded-none"
                  variant={"outline"}
                >
                  <span>{decodeHTML(cat.name)}</span>
                  <button>
                    <X
                      className="size-3 hover:text-neutral-400"
                      onClick={() => {
                        setSelectedCategories(
                          selectedCategories.filter(
                            (id) => id !== String(cat.id)
                          )
                        );
                        if (categoryId === String(cat.id)) {
                          nav(`/shop`);
                        }
                      }}
                    />
                  </button>
                </Badge>
              ))}
          </div>
          {categoryId === "503" ||
          categoryId === "504" ||
          categoryId === "500" ||
          categoryId === "501" ||
          selectedCategories ? (
            <div className="flex flex-col gap-2 mt-16">
              <h3 className="w-full text-center">
                Coming Soon!
              </h3>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <h3 className="w-full text-center">
                Products Not Found!
              </h3>
              <p className="text-sm">
                Try another filter instead
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
